import type {Dayjs} from 'dayjs';
import {DatePicker, theme} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import {getSearchLabelStyle} from 'Components/CustomTextInput/labelStyles';

interface IProps {
  date?: Date | null;
  handleChange: any;
  label?: string;
  flex?: number;
  minDate?: any;
  position?: 'end' | 'start' | undefined;
  reset?: any;
  inline?: boolean;
  disabled?: boolean;
  showTime?: boolean;
  // Use the /search page's compact label (12px, bold, secondary color).
  searchLabel?: boolean;
}

// Note: unlike the MUI version (date-fns + localeMap[tenantConfig.locale]), this
// doesn't wire the tenant's locale into antd's date formatting yet — antd's locale
// system works differently (ConfigProvider `locale`, not a date-fns Locale object).
// Known gap, same one flagged when SearchMember's Date of Birth field moved to antd.
const CustomDatePicker: React.FC<IProps> = (props) => {
  const {
    date,
    handleChange,
    label,
    flex = 1,
    minDate = null,
    inline,
    disabled = false,
    showTime = false,
    searchLabel = false,
  } = props;
  const {token}: any = theme.useToken();

  // Only forward real changes, since both onChange and onCalendarChange call this.
  const commitValue = (value: Dayjs | null) => {
    const next = value ? value.toDate() : null;
    const unchanged = next === date || (next && date && +new Date(date) === +next);
    if (!unchanged) handleChange(next);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex,
        marginRight: inline ? 16 : undefined,
      }}>
      {label && (
        <span
          style={
            searchLabel
              ? getSearchLabelStyle(token, disabled)
              : {
                  fontSize: 17,
                  fontWeight: 'bold',
                  color: disabled ? token.colorTextDisabled : token.colorTextBase,
                }
          }>
          {label}
        </span>
      )}
      <DatePicker
        value={date ? dayjs(date) : null}
        disabled={disabled}
        showTime={showTime}
        allowClear
        maxDate={dayjs()}
        {...(minDate && {minDate: dayjs(minDate)})}
        // antd reports a dayjs value (or null on clear); bridge back to a native
        // Date so every existing handleChange/formik.setFieldValue caller is unchanged.
        onChange={(value: Dayjs | null) => commitValue(value)}
        // Date+time pickers: commit on pick instead of only on "OK" (clicking outside
        // used to discard the selection), and also commit from onCalendarChange —
        // antd skips onChange when only the year differs (e.g. 28/09/2025 → 28/09/2024).
        {...(showTime && {
          needConfirm: false,
          onCalendarChange: (value: any) =>
            commitValue(Array.isArray(value) ? value[0] ?? null : value),
        })}
        style={{width: '100%'}}
      />
    </div>
  );
};

export default CustomDatePicker;
